import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Mail, Key, Copy, Download, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import QRCode from 'qrcode';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Modal from '../../../components/ui/Modal';
import { toast } from '../../../components/ui/Toast';
import twoFactorAuthService from '../../../services/twoFactorAuthService';

const TwoFactorSetup = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupModal, setSetupModal] = useState(false);
  const [setupMethod, setSetupMethod] = useState(null); // 'sms', 'email', 'authenticator'
  const [setupStep, setSetupStep] = useState(1); // 1: configure, 2: verify
  
  // Setup form data
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [qrCodeURL, setQrCodeURL] = useState('');
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await twoFactorAuthService?.get2FASettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading 2FA settings:', error);
      toast?.error('Failed to load 2FA settings');
    } finally {
      setLoading(false);
    }
  };

  const startSetup = (method) => {
    setSetupMethod(method);
    setSetupStep(1);
    setVerificationCode('');
    setSetupModal(true);
  };

  const handleSendVerification = async () => {
    setProcessing(true);
    try {
      if (setupMethod === 'sms') {
        if (!phoneNumber || phoneNumber?.length < 10) {
          toast?.error('Please enter a valid phone number');
          return;
        }
        await twoFactorAuthService?.setupSMS(phoneNumber);
        toast?.success('Verification code sent to your phone');
      } else if (setupMethod === 'email') {
        if (!email || !email?.includes('@')) {
          toast?.error('Please enter a valid email address');
          return;
        }
        await twoFactorAuthService?.setupEmail(email);
        toast?.success('Verification code sent to your email');
      } else if (setupMethod === 'authenticator') {
        const { secret, qrCodeURL: url } = await twoFactorAuthService?.setupAuthenticator();
        setTotpSecret(secret);
        setQrCodeURL(url);
        
        // Generate QR code image
        const qrImage = await QRCode?.toDataURL(url);
        setQrCodeImage(qrImage);
      }
      setSetupStep(2);
    } catch (error) {
      console.error('Error sending verification:', error);
      toast?.error('Failed to send verification code');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode?.length !== 6) {
      toast?.error('Please enter a valid 6-digit code');
      return;
    }

    setProcessing(true);
    try {
      const verified = await twoFactorAuthService?.verifySetup(setupMethod, verificationCode);
      
      if (verified) {
        toast?.success(`${setupMethod?.toUpperCase()} 2FA enabled successfully`);
        
        // Generate backup codes if this is the first method enabled
        if (!settings?.backup_codes_generated) {
          const codes = await twoFactorAuthService?.generateBackupCodes();
          setBackupCodes(codes);
          setShowBackupCodes(true);
        }
        
        // Enable 2FA if not already enabled
        if (!settings?.enabled) {
          await twoFactorAuthService?.enable2FA(setupMethod);
        }
        
        setSetupModal(false);
        loadSettings();
      } else {
        toast?.error('Invalid verification code. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      toast?.error('Failed to verify code');
    } finally {
      setProcessing(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!confirm('Are you sure you want to disable Two-Factor Authentication? This will make your account less secure.')) {
      return;
    }

    setProcessing(true);
    try {
      await twoFactorAuthService?.disable2FA();
      toast?.success('Two-Factor Authentication disabled');
      loadSettings();
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast?.error('Failed to disable 2FA');
    } finally {
      setProcessing(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!confirm('This will invalidate all existing backup codes. Continue?')) {
      return;
    }

    setProcessing(true);
    try {
      const codes = await twoFactorAuthService?.generateBackupCodes();
      setBackupCodes(codes);
      setShowBackupCodes(true);
      toast?.success('New backup codes generated');
    } catch (error) {
      console.error('Error generating backup codes:', error);
      toast?.error('Failed to generate backup codes');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    toast?.success('Copied to clipboard');
  };

  const downloadBackupCodes = () => {
    const text = backupCodes?.join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'backup-codes.txt';
    a?.click();
    URL.revokeObjectURL(url);
    toast?.success('Backup codes downloaded');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              Two-Factor Authentication
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Add an extra layer of security to your account
            </p>
          </div>
          <div className="flex items-center gap-2">
            {settings?.enabled ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-600">Enabled</span>
              </>
            ) : (
              <>
                <XCircle className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-600">Disabled</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 space-y-6">
        {/* 2FA Status */}
        {settings?.enabled && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-green-900 mb-1">2FA is Active</h3>
                <p className="text-sm text-green-700">
                  Your account is protected with two-factor authentication.
                  Default method: <strong>{settings?.default_method?.toUpperCase()}</strong>
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDisable2FA}
                disabled={processing}
              >
                Disable 2FA
              </Button>
            </div>
          </div>
        )}

        {/* Authentication Methods */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Authentication Methods</h3>

          {/* SMS */}
          <div className="border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Smartphone className="w-5 h-5 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-900">SMS Verification</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Receive verification codes via text message
                  </p>
                  {settings?.sms_enabled && settings?.sms_phone_number && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Configured: {settings?.sms_phone_number}
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant={settings?.sms_enabled ? 'outline' : 'primary'}
                onClick={() => startSetup('sms')}
              >
                {settings?.sms_enabled ? 'Reconfigure' : 'Setup'}
              </Button>
            </div>
          </div>

          {/* Email */}
          <div className="border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-900">Email Verification</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Receive verification codes via email
                  </p>
                  {settings?.email_enabled && settings?.email_address && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Configured: {settings?.email_address}
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant={settings?.email_enabled ? 'outline' : 'primary'}
                onClick={() => startSetup('email')}
              >
                {settings?.email_enabled ? 'Reconfigure' : 'Setup'}
              </Button>
            </div>
          </div>

          {/* Authenticator App */}
          <div className="border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Key className="w-5 h-5 text-blue-600 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-900">Authenticator App</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Use Google Authenticator, Authy, or similar apps
                  </p>
                  {settings?.authenticator_enabled && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Configured and active
                    </p>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant={settings?.authenticator_enabled ? 'outline' : 'primary'}
                onClick={() => startSetup('authenticator')}
              >
                {settings?.authenticator_enabled ? 'Reconfigure' : 'Setup'}
              </Button>
            </div>
          </div>
        </div>

        {/* Backup Codes */}
        {settings?.enabled && (
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Backup Codes</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Use backup codes if you lose access to your 2FA device
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRegenerateBackupCodes}
                disabled={processing}
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Regenerate Codes
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Setup Modal */}
      <Modal
        isOpen={setupModal}
        onClose={() => setSetupModal(false)}
        title={`Setup ${setupMethod?.toUpperCase() || ''} 2FA`}
        description="Configure your two-factor authentication method"
        icon={<Shield className="w-6 h-6" />}
      >
        <div className="space-y-4">
          {setupStep === 1 && (
            <>
              {setupMethod === 'sms' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e?.target?.value)}
                    placeholder="+1234567890"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Include country code (e.g., +1 for US)
                  </p>
                </div>
              )}

              {setupMethod === 'email' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    placeholder="your-email@example.com"
                  />
                </div>
              )}

              {setupMethod === 'authenticator' && (
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    Click below to generate a QR code for your authenticator app
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setSetupModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSendVerification} disabled={processing}>
                  {processing ? 'Processing...' : 'Continue'}
                </Button>
              </div>
            </>
          )}

          {setupStep === 2 && (
            <>
              {setupMethod === 'authenticator' && qrCodeImage && (
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-4">
                      Scan this QR code with your authenticator app
                    </p>
                    <img src={qrCodeImage} alt="QR Code" className="mx-auto border rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Or enter this secret key manually:
                    </label>
                    <div className="flex gap-2">
                      <Input value={totpSecret} readOnly />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(totpSecret)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {(setupMethod === 'sms' || setupMethod === 'email') && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-blue-800">
                    A verification code has been sent to your {setupMethod}.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter Verification Code
                </label>
                <Input
                  type="text"
                  maxLength="6"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e?.target?.value?.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setSetupModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleVerifyCode} disabled={processing}>
                  {processing ? 'Verifying...' : 'Verify & Enable'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Backup Codes Modal */}
      <Modal
        isOpen={showBackupCodes}
        onClose={() => setShowBackupCodes(false)}
        title="Backup Codes Generated"
        description="Save these backup codes in a secure location"
        icon={<Key className="w-6 h-6" />}
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> Save these backup codes in a secure location.
              Each code can only be used once.
            </p>
          </div>

          <div className="bg-gray-50 rounded p-4 font-mono text-sm space-y-2">
            {backupCodes?.map((code, index) => (
              <div key={index} className="flex items-center justify-between">
                <span>{code}</span>
                <button
                  onClick={() => copyToClipboard(code)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={downloadBackupCodes}>
              <Download className="w-4 h-4 mr-1" />
              Download
            </Button>
            <Button onClick={() => setShowBackupCodes(false)}>
              I've Saved My Codes
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TwoFactorSetup;